/**
 * EmployeeDetailPage — /projects/:projectId/workforce/:id
 * Demo: /manage-team (member detail) and /profile (header, status badges, underline tabs, right rail). Built on
 * DetailPage: the employee's name with status badges and actions, the assignment strip, URL-synced tabs (`?tab=`)
 * and the identity rail. Reassign still previews the impact and continues in the project conversation.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Badge, Button, Callout, Skeleton, StatusDot } from '../../components/ui';
import { DetailPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { useEmployee, useEmployeePermissionGrants } from '../../hooks/workforce';
import { useProject, useProjectCommandCenter } from '../../hooks/useProjects.js';
import { conversationHref } from '../projects/project-utils.js';
import { StatusBadge } from './shared';
import { AssignmentStrip } from './detail/AssignmentStrip.js';
import { DetailTabbed } from './detail/DetailTabbed.js';
import { EmployeePolicyCard } from './detail/EmployeePolicyCard.js';
import { IdentityRail } from './detail/IdentityRail.js';
import { EMPLOYEE_TAB_IDS, employeeTabs } from './detail/employee-tabs.js';
import { ReassignPreviewModal } from './reassign/ReassignPreviewModal.js';
import {
  buildProjectWorkforceLinks,
  decisionsFromResources,
  employeeSupervisionPrompt,
  isLinkedToProject,
  outputsFromResources,
  reassignPrompt,
  resourcesForEmployee,
  toProjectScopedEmployee,
  type ReassignDraft,
} from './project-context.js';

export const EmployeeDetailPage: React.FC = () => {
  const { projectId, id } = useParams<{ projectId?: string; id: string }>();
  const navigate = useNavigate();
  const { data: rawEmployee, isLoading, error } = useEmployee(id);
  const { data: grants = [] } = useEmployeePermissionGrants(id);
  const { data: project, isLoading: projectLoading, error: projectError } = useProject(projectId);
  const { data: commandCenter } = useProjectCommandCenter(projectId);
  const [tab, setTab] = useTabParam(EMPLOYEE_TAB_IDS, 'work');
  const [reassignOpen, setReassignOpen] = useState(false);
  const projectLinks = buildProjectWorkforceLinks(commandCenter);
  const rosterHref = project ? `/projects/${project.id}/workforce` : '/projects';
  const linkedToProject = rawEmployee ? isLinkedToProject(rawEmployee, projectLinks) : false;
  const employee =
    rawEmployee && project ? toProjectScopedEmployee(rawEmployee, project.name, linkedToProject) : rawEmployee;
  const relatedResources = rawEmployee ? resourcesForEmployee(commandCenter, rawEmployee.id) : [];
  const outputs = outputsFromResources(relatedResources);
  const decisions = decisionsFromResources(relatedResources);
  const outputsHref = project ? `/projects/${project.id}/work-threads` : null;

  const openConversation = () => {
    if (!project || !employee) return;
    navigate(conversationHref(project, employeeSupervisionPrompt(project, employee)));
  };

  const continueReassign = (draft: ReassignDraft) => {
    if (!project || !employee) return;
    setReassignOpen(false);
    navigate(conversationHref(project, reassignPrompt(project, employee, draft)));
  };

  if (isLoading || projectLoading) {
    return (
      <div className="space-y-5" aria-busy="true">
        <Skeleton height={28} width="30%" />
        <Skeleton height={40} />
        <Skeleton height={220} rounded="rounded-xl" />
      </div>
    );
  }

  if (error || !employee || projectError || !project || !projectId) {
    return (
      <DetailPage
        title="Employee"
        breadcrumbs={[
          { label: 'Projects', to: '/projects' },
          { label: 'Workforce', to: rosterHref },
        ]}
        alert={
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load this employee"
            actions={
              <Button size="sm" variant="secondary" onClick={() => navigate(rosterHref)}>
                Back to workforce
              </Button>
            }
          >
            The employee or its project could not be loaded. Open the employee from a project's workforce list.
          </Callout>
        }
      >
        {null}
      </DetailPage>
    );
  }

  return (
    <>
      <DetailPage
        title={employee.name}
        subtitle={`${employee.role} · ${employee.tier}. Reusable by other projects.`}
        breadcrumbs={[
          { label: 'Projects', to: '/projects' },
          { label: project.name, to: `/projects/${project.id}` },
          { label: 'Workforce', to: rosterHref },
        ]}
        helpContent={PAGE_HELP['workforce']}
        badges={
          <>
            <StatusBadge status={employee.status} label={employee.statusLabel} />
            {employee.team && <Badge variant="neutral">{employee.team}</Badge>}
            <Badge variant={linkedToProject ? 'info' : 'neutral'}>
              {linkedToProject ? `Linked to ${project.name}` : 'Reusable org employee'}
            </Badge>
            <Badge
              variant={employee.kind === 'ai' ? 'info' : 'neutral'}
              icon={<StatusDot variant={employee.kind === 'ai' ? 'info' : 'neutral'} size="sm" />}
            >
              {employee.kind === 'ai' ? 'AI worker' : 'Human'}
            </Badge>
          </>
        }
        actions={
          <>
            <Can policy="CanManageWorkforce">
              <Button variant="secondary" onClick={() => setReassignOpen(true)}>
                Reassign
              </Button>
            </Can>
            <Button onClick={openConversation}>Open conversation</Button>
          </>
        }
        alert={<EmployeePolicyCard employee={employee} />}
        meta={<AssignmentStrip employee={employee} projectName={project.name} linkedToProject={linkedToProject} />}
        tabs={employeeTabs({
          outputs: outputs.length || undefined,
          decisions: decisions.length || undefined,
          perms: grants.length || undefined,
        })}
        activeTab={tab}
        onTabChange={setTab}
        rail={<IdentityRail employee={employee} projectName={project.name} linkedToProject={linkedToProject} />}
      >
        <DetailTabbed
          tab={tab}
          employee={employee}
          grants={grants}
          linkedToProject={linkedToProject}
          outputs={outputs}
          outputsHref={outputsHref}
          decisions={decisions}
          onOpenConversation={openConversation}
        />
      </DetailPage>

      <ReassignPreviewModal
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        employee={employee}
        projectName={project.name}
        onContinue={continueReassign}
      />
    </>
  );
};
